/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import LegalPageLayout from "./LegalPageLayout";

export default function TermsPage() {
  return (
    <LegalPageLayout
      testid="page-terms"
      eyebrow="— Legal"
      title="Terms of Use"
      subtitle="The agreement between you and DONiTSH!T when you use the wellness ecosystem."
      lastUpdated="August 2026"
    >
      <h2>1. About DONiTSH!T</h2>
      <p>
        DONiTSH!T is a premium multilingual wellness ecosystem. The
        website and companion applications provide educational and
        lifestyle content, an AI wellness assistant ("Ask Donny"), a
        Dosha Test, a wellness marketplace connecting users with
        independent practitioners and businesses, a shop, and related
        tools.
      </p>
      <p>
        <strong>
          DONiTSH!T is not a medical provider, therapist, or emergency
          service.
        </strong>{" "}
        See our <Link to="/disclaimer">Health & Wellness Disclaimer</Link>.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 13 years old to use the general DONiTSH!T
        website. If you are between 13 and the age of majority in your
        jurisdiction, you must have permission from a parent or legal
        guardian.
      </p>
      <p>
        Certain experiences in the DONiTSH!T mobile apps that involve
        adult relationship content or user-to-user connection through{" "}
        <strong>Luv Connect</strong> or <strong>Couples Connect</strong>{" "}
        are restricted to users <strong>18 years of age or older</strong>{" "}
        and are gated within those mobile experiences. On this website,
        Luv Connect and Couples Connect are educational pages that
        describe those experiences; they do not currently host
        user-to-user matching, messaging, or adult content.
      </p>

      <h2>3. Your Account</h2>
      <p>
        You are responsible for the accuracy of your account information
        and for maintaining the confidentiality of your credentials. You
        are responsible for all activity that occurs under your account.
        Notify us immediately of any unauthorized use.
      </p>

      <h2>4. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for any illegal purpose</li>
        <li>
          Post or submit abusive, harassing, threatening, discriminatory,
          hateful, sexually exploitative, or otherwise objectionable
          content
        </li>
        <li>Impersonate any person or business</li>
        <li>
          Attempt to bypass authentication, access controls, or the
          marketplace approval workflow
        </li>
        <li>
          Scrape, mirror, or resell content or listings without written
          permission
        </li>
        <li>Interfere with the security or integrity of the Service</li>
      </ul>
      <p>
        See our{" "}
        <Link to="/community-guidelines">Community Guidelines</Link>{" "}
        for detailed rules on acceptable content and behavior.
      </p>

      <h2>5. AI Assistant ("Ask Donny")</h2>
      <p>
        Ask Donny is an educational AI assistant. It surfaces information
        drawn from the DONiTSH!T library and general wellness sources,
        and may cite authoritative references. It does not provide
        medical diagnosis, treatment, or individualized clinical advice.
        See our <Link to="/disclaimer">Health & Wellness Disclaimer</Link>.
      </p>

      <h2>6. Marketplace and Business Listings</h2>
      <ul>
        <li>
          Practitioners and businesses listed in the DONiTSH!T
          Marketplace are{" "}
          <strong>independent third parties</strong>. DONiTSH!T does not
          employ them and does not guarantee their qualifications,
          availability, pricing, or outcomes.
        </li>
        <li>
          Business listings go through an approval workflow before being
          published. Approval does not constitute endorsement.
        </li>
        <li>
          Bookings and inquiries submitted through the marketplace are
          messages sent to the listed business; contracting and payment
          for services occur directly between you and that business
          unless expressly stated otherwise.
        </li>
        <li>
          Member Benefits (promo codes and offers) are provided by
          partner businesses and are subject to each offer's own terms.
        </li>
      </ul>

      <h2>7. Shop and Purchases</h2>
      <p>
        Physical and digital products offered through the DONiTSH!T Shop
        are sold and fulfilled through our Shopify storefront. Purchases
        are governed by Shopify's checkout and by any product-specific
        terms surfaced at the time of purchase.
      </p>

      <h2>8. Subscriptions, Auto-Renewal, and Billing</h2>

      <h3>Mobile apps (Apple App Store and Google Play)</h3>
      <p>
        The DONiTSH!T mobile applications may offer auto-renewable
        subscriptions through the Apple App Store and/or Google Play.
        Where such subscriptions are offered:
      </p>
      <ul>
        <li>
          <strong>Apple App Store subscriptions</strong> are billed
          through your Apple ID. Subscriptions{" "}
          <strong>
            renew automatically at the end of each billing period
          </strong>{" "}
          unless auto-renewal is turned off at least 24 hours before the
          current period ends. Subscriptions can be managed, changed, or
          cancelled only in your Apple ID account settings, in
          accordance with Apple's subscription terms.
        </li>
        <li>
          <strong>Google Play subscriptions</strong> are billed through
          your Google Play account. Subscriptions{" "}
          <strong>
            renew automatically at the end of each billing period
          </strong>{" "}
          unless auto-renewal is turned off before the current period
          ends. Subscriptions can be managed, changed, or cancelled only
          in your Google Play subscriptions settings, in accordance with
          Google Play's subscription terms.
        </li>
        <li>
          Applicable subscription tiers, features, billing period, and
          price are presented in the mobile app at the time of purchase
          and are set by the applicable app store. Pricing may vary by
          country or region and is controlled by Apple or Google, not by
          DONiTSH!T on this website.
        </li>
        <li>
          By subscribing through the Apple App Store or Google Play, you
          also agree to the applicable{" "}
          <strong>Apple Media Services / EULA</strong> or{" "}
          <strong>Google Play Terms of Service</strong>, which govern
          your subscription in addition to these Terms.
        </li>
      </ul>

      <h3>Website (donitshit.com)</h3>
      <p>
        At the time of publication, the DONiTSH!T{" "}
        <strong>website does not offer a live subscription checkout</strong>.
        Purchases available on the website through the DONiTSH!T Shop
        are <strong>one-time product transactions</strong> handled by
        Shopify. If a Stripe-processed purchase or subscription is
        introduced on the website in the future, its billing period,
        price, renewal behavior, and cancellation instructions will be
        presented at the time of purchase.
      </p>
      <p>
        The absence of a subscription checkout on the website does{" "}
        <strong>not</strong> mean that DONiTSH!T does not offer
        subscriptions overall — active subscriptions, when offered, are
        provided through the mobile applications as described above.
      </p>

      <h2>9. Refunds and Cancellations</h2>
      <ul>
        <li>
          <strong>Shop (Shopify) purchases:</strong> refunds and
          cancellations follow Shopify's checkout policy and the
          applicable product policy shown at purchase.
        </li>
        <li>
          <strong>App Store / Google Play subscriptions:</strong> all
          refund and cancellation requests must be handled through the
          respective app store, in accordance with Apple's and Google's
          policies.
        </li>
        <li>
          <strong>Stripe web charges:</strong> where introduced in the
          future, refund terms will be presented at the time of purchase.
        </li>
        <li>
          <strong>Marketplace bookings:</strong> refunds and
          cancellations for services booked with independent
          practitioners or businesses are handled directly by those
          third parties.
        </li>
      </ul>

      <h2>10. Health and Wellness</h2>
      <p>
        The Service is for educational and general wellness purposes
        only and is not a substitute for advice from a qualified
        healthcare professional. DONiTSH!T does not provide medical
        diagnosis or treatment. See the full{" "}
        <Link to="/disclaimer">Health & Wellness Disclaimer</Link>.
      </p>

      <h2>11. Termination</h2>
      <p>
        You may stop using the Service at any time. We may suspend or
        terminate access for violations of these Terms or our Community
        Guidelines. To close your account, follow the process described
        in our <Link to="/privacy">Privacy Policy</Link> under "Account
        and Data Deletion."
      </p>

      <h2>12. Disclaimers and Limitation of Liability</h2>
      <p>
        The Service is provided "as is" and "as available." To the
        maximum extent permitted by law, DONiTSH!T disclaims all
        warranties, express or implied. DONiTSH!T is not liable for
        indirect, incidental, special, consequential, or punitive
        damages, or for any loss of profits or data, arising out of your
        use of the Service.
      </p>

      <h2>13. Governing Law</h2>
      <p>
        These Terms are governed by the laws applicable at DONiTSH!T's
        operating jurisdiction, without regard to conflict-of-laws
        rules.
      </p>

      <h2>14. Changes</h2>
      <p>
        We may update these Terms from time to time. Material changes
        will be reflected by the "Last updated" date at the top.
      </p>

      <h2>15. Contact</h2>
      <p>
        Reach us through the <Link to="/support">Support page</Link>{" "}
        contact form (topic: "Legal").
      </p>
    </LegalPageLayout>
  );
}
